/**
 * LiteSabreSCM — tenant-scoped Source Control Management integrations.
 *
 * Admins add per-provider PATs (GitHub, GitLab, Bitbucket Cloud). Tokens are
 * encrypted at rest on the backend (Fernet keyed off SECRET_KEY) and never
 * returned in cleartext. Enables private-repo scanning via API — no ``git
 * clone``, works uniformly across environments.
 *
 * Also exposes a "Browse repositories" panel: once a token is saved, users
 * can list every repo the PAT can access and trigger a scan with one click.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../services/api';
import { Github, Gitlab, Loader2, ShieldCheck, ShieldAlert, Trash2, Play, ExternalLink } from 'lucide-react';

const PROVIDER_META = {
  github:    { label: 'GitHub',    icon: Github, color: 'text-white',  hint: 'Personal Access Token (classic) with repo scope. Enterprise Server users override Base URL.' },
  gitlab:    { label: 'GitLab',    icon: Gitlab, color: 'text-orange-400', hint: 'Personal Access Token with api + read_repository scopes. Self-hosted users override Base URL.' },
  bitbucket: { label: 'Bitbucket', icon: null,   color: 'text-blue-400',   hint: 'App Password with Repositories: Read. Bitbucket Cloud requires the account username too.' },
};

const ProviderRow = ({ provider, integration, tenantId, onChanged }) => {
  const meta = PROVIDER_META[provider];
  const [expanded, setExpanded] = useState(!integration);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [browsing, setBrowsing] = useState(false);
  const [repos, setRepos] = useState([]);
  const [scanning, setScanning] = useState('');
  const [form, setForm] = useState({ token: '', username: '', base_url: '' });
  const navigate = useNavigate();

  const configured = Boolean(integration);
  const Icon = meta.icon;

  const save = async () => {
    if (!form.token.trim()) return toast.error('Token is required');
    setSaving(true);
    try {
      await api.put(`/litesabre/scm/integrations/${provider}`, {
        token: form.token.trim(),
        username: form.username.trim() || undefined,
        base_url: form.base_url.trim() || undefined,
      });
      toast.success(`${meta.label} credentials saved (encrypted). Click Test to verify.`);
      setForm({ token: '', username: '', base_url: '' });
      setExpanded(false);
      setTestResult(null);
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const r = await api.post(`/litesabre/scm/integrations/${provider}/test`);
      setTestResult(r.data);
      if (r.data.verdict === 'pass') toast.success(`Authenticated as ${r.data.identity?.login || 'user'}`);
      else toast.error(r.data.message || 'Provider rejected the token');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally { setTesting(false); }
  };

  const browseRepos = async () => {
    setBrowsing(true);
    try {
      const r = await api.get(`/litesabre/scm/integrations/${provider}/repositories?limit=50`);
      setRepos(r.data.repositories || []);
      toast.success(`Found ${r.data.count} repositories`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch repositories');
    } finally { setBrowsing(false); }
  };

  const scanRepo = async (repo) => {
    setScanning(repo.full_name);
    try {
      const r = await api.post('/litesabre/scm/scan', {
        repository_url: repo.web_url || repo.clone_url,
        branch: repo.default_branch,
      });
      toast.success(`Scan complete: ${r.data.findings_count} findings on ${r.data.files_scanned} files`);
      if (r.data.scan_id) navigate(`/litesabre/scans/${r.data.scan_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally { setScanning(''); }
  };

  const clear = async () => {
    if (!window.confirm(`Remove ${meta.label} credentials for this tenant?`)) return;
    try {
      await api.delete(`/litesabre/scm/integrations/${provider}`);
      toast.success('Credentials cleared');
      setRepos([]);
      setTestResult(null);
      await onChanged();
    } catch (e) { toast.error('Failed to remove'); }
  };

  return (
    <Card className="bg-slate-800 border-slate-700 overflow-hidden" data-testid={`scm-provider-${provider}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div className="flex items-center gap-3">
          {Icon ? <Icon className={`w-5 h-5 ${meta.color}`} /> : <span className={`text-lg font-bold ${meta.color}`}>Ⓑ</span>}
          <div>
            <CardTitle className="text-base text-white">{meta.label}</CardTitle>
            <p className="text-xs text-slate-400 mt-0.5">{meta.hint}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {configured ? (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-400" data-testid={`scm-${provider}-configured`}>
              <ShieldCheck className="w-3.5 h-3.5" /> Configured
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-amber-400">
              <ShieldAlert className="w-3.5 h-3.5" /> Not configured
            </span>
          )}
          <Button size="sm" variant="outline" onClick={() => setExpanded((v) => !v)}
                  data-testid={`scm-${provider}-toggle`}
                  className="border-slate-600 text-slate-200">
            {expanded ? 'Hide' : configured ? 'Manage' : 'Configure'}
          </Button>
        </div>
      </CardHeader>
      {configured && (
        <div className="px-6 pb-3 flex flex-wrap gap-2 text-xs text-slate-400">
          <span>Token: <span className="font-mono text-slate-300">{integration.token_masked}</span></span>
          {integration.username && <span>· User: <span className="font-mono">{integration.username}</span></span>}
          {integration.base_url && <span>· Base: <span className="font-mono">{integration.base_url}</span></span>}
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={runTest} disabled={testing}
                    data-testid={`scm-${provider}-test`}
                    className="text-xs border-indigo-500 text-indigo-300">
              {testing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
              Test Connection
            </Button>
            <Button size="sm" variant="outline" onClick={browseRepos} disabled={browsing}
                    data-testid={`scm-${provider}-browse`}
                    className="text-xs border-emerald-500 text-emerald-300">
              {browsing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
              Browse Repos
            </Button>
          </div>
        </div>
      )}
      {testResult && (
        <div className={`mx-6 mb-3 p-2 rounded text-xs ${testResult.verdict === 'pass' ? 'bg-emerald-500/10 text-emerald-300' : 'bg-rose-500/10 text-rose-300'}`}
             data-testid={`scm-${provider}-test-result`}>
          {testResult.verdict === 'pass'
            ? `✓ Authenticated as ${testResult.identity?.login} (${testResult.identity?.name || ''})`
            : `✗ ${testResult.message}`}
        </div>
      )}
      {expanded && (
        <CardContent className="border-t border-slate-700 bg-slate-900 space-y-3 py-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Personal Access Token</label>
            <Input type="password" value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })}
                   placeholder={provider === 'bitbucket' ? 'App password' : 'ghp_...  or  glpat-...'}
                   data-testid={`scm-${provider}-token-input`}
                   className="bg-slate-800 border-slate-700 text-white font-mono text-sm" />
          </div>
          {provider === 'bitbucket' && (
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Bitbucket Username</label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                     placeholder="myaccount"
                     data-testid={`scm-${provider}-username-input`}
                     className="bg-slate-800 border-slate-700 text-white text-sm" />
            </div>
          )}
          {(provider === 'github' || provider === 'gitlab') && (
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Base URL (leave blank for cloud)</label>
              <Input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                     placeholder={provider === 'github' ? 'https://github.mycorp.com/api/v3' : 'https://gitlab.mycorp.com/api/v4'}
                     data-testid={`scm-${provider}-baseurl-input`}
                     className="bg-slate-800 border-slate-700 text-white text-sm font-mono" />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={saving}
                    data-testid={`scm-${provider}-save`}
                    className="bg-indigo-600 hover:bg-indigo-700">
              {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Save (Encrypted)
            </Button>
            {configured && (
              <Button size="sm" variant="outline" onClick={clear}
                      data-testid={`scm-${provider}-clear`}
                      className="border-rose-500 text-rose-400">
                <Trash2 className="w-3 h-3 mr-1" /> Remove
              </Button>
            )}
          </div>
        </CardContent>
      )}
      {repos.length > 0 && (
        <CardContent className="border-t border-slate-700">
          <p className="text-xs text-slate-400 mb-2">
            Showing {repos.length} accessible repos — click Scan to secret-scan any of them.
          </p>
          <div className="space-y-1 max-h-80 overflow-y-auto" data-testid={`scm-${provider}-repo-list`}>
            {repos.map((r) => (
              <div key={r.full_name} className="flex items-center justify-between py-2 px-3 rounded hover:bg-slate-900 border border-transparent hover:border-slate-700">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white font-medium truncate">{r.full_name}</span>
                    {r.private && <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded">private</span>}
                    <a href={r.web_url} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-indigo-400"><ExternalLink className="w-3 h-3" /></a>
                  </div>
                  {r.description && <p className="text-xs text-slate-500 truncate">{r.description}</p>}
                </div>
                <Button size="sm" onClick={() => scanRepo(r)} disabled={scanning === r.full_name}
                        data-testid={`scm-scan-repo-${r.full_name.replace(/[^a-zA-Z0-9]/g, '_')}`}
                        className="bg-emerald-600 hover:bg-emerald-700 text-xs ml-2">
                  {scanning === r.full_name ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                  Scan
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const LiteSabreSCM = () => {
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState('');
  const [integrations, setIntegrations] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/litesabre/scm/integrations');
      setTenantId(r.data.tenant_id);
      setIntegrations(r.data.integrations || []);
    } catch (e) {
      toast.error('Failed to load SCM integrations');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const byProvider = useMemo(() => {
    const m = {};
    integrations.forEach((i) => { m[i.provider] = i; });
    return m;
  }, [integrations]);

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4" data-testid="litesabre-scm-page">
      <div>
        <h1 className="text-2xl font-bold">Source Control Integrations</h1>
        <p className="text-gray-500 mt-1">
          Connect private GitHub, GitLab, or Bitbucket repositories to LiteSabre.
          Tokens are encrypted at rest and isolated to this tenant.
        </p>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-3">
          {['github', 'gitlab', 'bitbucket'].map((p) => (
            <ProviderRow key={p} provider={p} integration={byProvider[p]} tenantId={tenantId} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LiteSabreSCM;
